import React, { useState, useRef } from 'react'
import ReactDOM from 'react-dom'

import Button from '@react/react-spectrum/Button'
import Dialog from '@react/react-spectrum/Dialog'
import ModalTrigger from '@react/react-spectrum/ModalTrigger'
import Textfield from '@react/react-spectrum/Textfield'
import { success, error, Toast } from '@react/react-spectrum/Toast'

import Copy from '@react/react-spectrum/Icon/Copy'
import Refresh from '@react/react-spectrum/Icon/Refresh'

import { PATHS } from '../../config/constants'
import { getPreviewToken } from 'services/project'

export const ShareProject = ({ project, projectConfig }) => {
  return (
    <ModalTrigger>
      <Button>Share</Button>
      <ShareProjectDialog project={project} projectConfig={projectConfig} />
    </ModalTrigger>
  )
}

const getClientPath = (type) => {
  switch (type) {
    case 'web':
      return PATHS.WEB_PROJECT
    case 'web-modular':
    default:
      return PATHS.WEB_MODULAR_PROJECT
  }
}

const copyInputByRef = (elementRef) => {
  const input = ReactDOM.findDOMNode(elementRef.current)
  input.select()
  input.setSelectionRange(0, 99999)
  document.execCommand('copy')
  success('URL copied to clipboard')
}

export const ShareProjectDialog = ({ project, projectConfig, ...otherProps }) => {
  const projectUrl = useRef()
  const websiteUrl = useRef()
  const [websiteAccessToken, setWebsiteAccessToken] = useState('')

  const clientPath = getClientPath(project.meta.type)

  const isPrivate = !project?.isPublic && !project?.isFeatured
  const projectAccessUrl = ' '.concat(window.location.origin, PATHS.PROJECT_DETAILS, project?.meta?.id)
  const websiteAccessUrl = websiteAccessToken
    ? ' '.concat(window.location.origin, clientPath, project?.meta?.id, '?token=', websiteAccessToken)
    : ''

  const disableLinkGeneration = isPrivate && window.DSN?.currentUser?.id !== project?.owner
  const generateWebsiteUrl = () => {
    getPreviewToken(project?.meta?.id)
      .then((token) => setWebsiteAccessToken(token || ''))
      .catch((e) => error('Could not generate access token'))
  }

  return (
    <Dialog title='Share project' confirmLabel='Done' {...otherProps} role='dialog' className='shareDialog'>
      {isPrivate && (
        <Toast
          variant='warning'
          actionLabel='Read More'
          onAction={() => window.open('https://docs.adobedemo.com/projects/access-permissions', '_blank')}
        >
          This project is private, so only users selected by the owner can view and edit it.
        </Toast>
      )}
      <section>
        <p>Share this URL with anyone that should have access to your project's builder view:</p>
        <Textfield readOnly={true} value={projectAccessUrl} ref={projectUrl} />
        <Button variant='action' icon={<Copy />} onClick={() => copyInputByRef(projectUrl)} />
      </section>

      {['web', 'web-modular'].includes(project.meta.type) && (
        <section>
          <p>To share the website created by this project, generate a preview-only link that will expire in time.</p>
          <Textfield readOnly={true} value={websiteAccessUrl} ref={websiteUrl} />
          <Button variant='action' icon={<Copy />} onClick={() => copyInputByRef(websiteUrl)} />
          <Button
            variant='action'
            icon={<Refresh />}
            label='Generate Link'
            onClick={generateWebsiteUrl}
            disabled={disableLinkGeneration}
          />
        </section>
      )}

      {project.meta.type === 'mobile' && (
        <section>
          <p>Use this project ID to load your mobile app content in any DX Demo app</p>
          <Textfield readOnly={true} value={project?.meta?.id} ref={websiteUrl} />
          <Button variant='action' icon={<Copy />} onClick={() => copyInputByRef(websiteUrl)} />
        </section>
      )}
    </Dialog>
  )
}
